import { TFunction } from 'i18next';
import { DeleteBlockingReason, DeleteEffect } from '../codegen/graphql';

export interface DeleteNodeMessaging {
    warning: string;
    confirmLabel: string;
    /** Trashing is recoverable and confirms like a normal action (primary);
     *  only the irreversible purge gets the error color. */
    confirmColor: 'error' | 'primary';
    /** PURGE is irreversible — the confirm needs an explicit acknowledgement. */
    requiresAcknowledgement: boolean;
}

/**
 * Copy for the delete-node dialog, driven by the preview's effect: the same
 * button on the same node either moves it to the trash (recoverable) or
 * purges it irreversibly, and the wording must never claim the wrong one.
 */
export const getDeleteNodeMessaging = (
    args: { effect: DeleteEffect; isLeaf: boolean; nodeName?: string | null },
    t: TFunction,
): DeleteNodeMessaging => {
    const { effect, isLeaf, nodeName } = args;

    if (effect === DeleteEffect.TRASH) {
        return {
            warning: isLeaf
                ? t('You are about to move {{nodeName}} to the trash. You can restore it later by moving it out of the trash.', { nodeName })
                : t('You are about to move {{nodeName}} to the trash. You can either move all of its sub-nodes along with it, or move only this node and reassign its sub-nodes to another parent node. Trashed elements can be restored later by moving them out of the trash.', { nodeName }),
            confirmLabel: t('Move to trash'),
            confirmColor: 'primary',
            requiresAcknowledgement: false,
        };
    }

    return {
        warning: isLeaf
            ? t('You are about to permanently delete {{nodeName}}. This action cannot be undone.', { nodeName })
            : t('You are about to permanently delete {{nodeName}}. You can either delete all of its sub-nodes along with it, or delete only this node and move its sub-nodes to another parent node. This action cannot be undone.', { nodeName }),
        confirmLabel: t('Delete permanently'),
        confirmColor: 'error',
        requiresAcknowledgement: true,
    };
};

/**
 * Localized copy per blocking reason. HAS_INSTALLED_ASSETS carries the
 * preview's count, which is scoped to exactly the nodes this call would act
 * on (children rescued to a target parent are not counted).
 */
export const getDeleteBlockingReasonLabels = (
    installedAssetCount: number,
    t: TFunction,
): Record<DeleteBlockingReason, string> => ({
    [DeleteBlockingReason.HAS_INSTALLED_ASSETS]: t('The selection contains {{count}} installed asset(s). Installed assets must be removed first via an event.', { count: installedAssetCount }),
    [DeleteBlockingReason.PROTECTED_NODE]: t('Protected elements such as company roots, pools and the trash cannot be deleted.'),
    [DeleteBlockingReason.MISSING_WRITE_ACCESS]: t('You need write access to the node and to the target parent node for its sub-nodes.'),
    [DeleteBlockingReason.LAST_PRESSURE_LINE]: t('The last pressure line of a connected system cannot be removed.'),
    [DeleteBlockingReason.CROSS_CLIENT_TARGET]: t('The target parent node belongs to a different customer.'),
    [DeleteBlockingReason.INVALID_RESCUE_TARGET]: t('The target parent node cannot hold the sub-nodes that would be moved to it. Choose a parent that accepts their node types.'),
});
